import { Popover, Transition } from '@headlessui/react';
import {
  CheckIcon,
  ChevronDownIcon,
  SearchIcon,
  XIcon,
} from '@heroicons/react/outline';
import React, { Fragment, useMemo, useState } from 'react';

import clsxm from '@/lib/clsxm';
import { normalizeSearchText } from '@/lib/voiceFilters';

export interface FilterMenuOption {
  value: string;
  label: string;
  flag?: string;
  /** Already formatted for the user's locale. */
  count?: string;
  disabled?: boolean;
}

interface FilterMenuProps {
  value: string;
  /** Shown when nothing is picked, and as the first option ("All languages"). */
  placeholder: string;
  options: FilterMenuOption[];
  onChange: (value: string) => void;
  /** Adds a search field to the menu (long lists such as languages). */
  searchPlaceholder?: string;
  noMatchesLabel: string;
  clearLabel: string;
  icon?: React.ReactNode;
}

/** A dropdown for one filter: the picked value, a clear button, a searchable list. */
const FilterMenu = ({
  value,
  placeholder,
  options,
  onChange,
  searchPlaceholder,
  noMatchesLabel,
  clearLabel,
  icon,
}: FilterMenuProps) => {
  const [query, setQuery] = useState('');
  const selected = options.find((option) => option.value === value);

  const visible = useMemo(() => {
    const needle = normalizeSearchText(query.trim());
    if (!needle) return options;
    return options.filter(
      (option) =>
        normalizeSearchText(option.label).includes(needle) ||
        normalizeSearchText(option.value).includes(needle),
    );
  }, [options, query]);

  const optionClass = (active: boolean, disabled?: boolean) =>
    clsxm(
      'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
      'focus:outline-none focus-visible:bg-gray-100 dark:focus-visible:bg-white/10',
      active
        ? 'bg-gray-100 font-medium text-gray-900 dark:bg-white/10 dark:text-white'
        : 'text-gray-700 hover:bg-gray-50 dark:text-slate-300 dark:hover:bg-white/5',
      disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
    );

  return (
    <Popover className='relative'>
      {({ open, close }) => (
        <>
          <Popover.Button
            className={clsxm(
              'flex h-10 w-full items-center gap-2 rounded-xl border bg-white pl-3 pr-2.5 text-left text-sm transition',
              'focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-500/20 dark:bg-white/5',
              open
                ? 'border-gray-400 ring-4 ring-gray-900/5 dark:border-white/30'
                : 'border-gray-200 hover:border-gray-300 dark:border-white/10 dark:hover:border-white/20',
            )}
          >
            <span className='flex w-5 shrink-0 justify-center text-base leading-none'>
              {selected?.flag || icon}
            </span>
            <span
              className={clsxm(
                'min-w-0 flex-1 truncate',
                selected
                  ? 'font-medium text-gray-900 dark:text-white'
                  : 'text-gray-500 dark:text-slate-400',
              )}
            >
              {selected ? selected.label : placeholder}
            </span>
            {/* Room for the clear button, which sits outside this button. */}
            {selected && <span className='w-6 shrink-0' />}
            <ChevronDownIcon
              className={clsxm(
                'h-4 w-4 shrink-0 text-gray-400 transition-transform',
                open && 'rotate-180',
              )}
            />
          </Popover.Button>
          {selected && (
            <button
              type='button'
              aria-label={clearLabel}
              onClick={() => onChange('')}
              className='absolute right-8 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-white'
            >
              <XIcon className='h-3.5 w-3.5' />
            </button>
          )}
          <Transition
            as={Fragment}
            enter='transition ease-out duration-100'
            enterFrom='opacity-0 -translate-y-1'
            enterTo='opacity-100 translate-y-0'
            leave='transition ease-in duration-75'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
            afterLeave={() => setQuery('')}
          >
            <Popover.Panel className='absolute left-0 right-0 z-30 mt-1.5 rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl shadow-gray-900/10 dark:border-white/10 dark:bg-slate-800 dark:shadow-black/40'>
              {searchPlaceholder && (
                <div className='relative mb-1'>
                  <SearchIcon className='pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
                  <input
                    autoFocus
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter') return;
                      event.preventDefault();
                      const first = visible.find((option) => !option.disabled);
                      if (first) {
                        onChange(first.value);
                        close();
                      }
                    }}
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                    className='h-9 w-full rounded-lg border-0 bg-gray-50 pl-8 pr-2 text-sm text-gray-900 placeholder-gray-400 focus:bg-gray-100 focus:outline-none focus:ring-0 dark:bg-white/5 dark:text-white dark:placeholder-slate-500 dark:focus:bg-white/10'
                  />
                </div>
              )}
              <div className='max-h-64 overflow-y-auto overscroll-contain'>
                {!query && (
                  <button
                    type='button'
                    onClick={() => {
                      onChange('');
                      close();
                    }}
                    className={optionClass(!value)}
                  >
                    <span className='flex w-5 shrink-0 justify-center text-gray-400'>
                      {icon}
                    </span>
                    <span className='min-w-0 flex-1 truncate'>
                      {placeholder}
                    </span>
                    {!value && <CheckIcon className='h-4 w-4 shrink-0' />}
                  </button>
                )}
                {visible.map((option) => {
                  const active = option.value === value;
                  return (
                    <button
                      key={option.value}
                      type='button'
                      disabled={option.disabled && !active}
                      onClick={() => {
                        onChange(option.value);
                        close();
                      }}
                      className={optionClass(
                        active,
                        option.disabled && !active,
                      )}
                    >
                      <span className='flex w-5 shrink-0 justify-center text-base leading-none'>
                        {option.flag}
                      </span>
                      <span className='min-w-0 flex-1 truncate'>
                        {option.label}
                      </span>
                      {option.count && (
                        <span className='shrink-0 text-xs tabular-nums text-gray-400 dark:text-slate-500'>
                          {option.count}
                        </span>
                      )}
                      {active && <CheckIcon className='h-4 w-4 shrink-0' />}
                    </button>
                  );
                })}
                {visible.length === 0 && (
                  <p className='px-2.5 py-6 text-center text-sm text-gray-400 dark:text-slate-500'>
                    {noMatchesLabel}
                  </p>
                )}
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default FilterMenu;
